import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  INSTALMENT_BODY_LABELS,
  INSTALMENT_METHOD_LABELS,
  INSTALMENT_STATE_LABELS,
  type InstalmentPlanFigures,
  type InstalmentPlanListItem,
  type InstalmentPlanResponse,
} from '@k-management/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { InstalmentPlanForm } from './InstalmentPlanForm.js';
import { announceTreasuryChange, movementPath } from './treasury-ui.js';

const keys = {
  all: ['treasury', 'instalment-plans'] as const,
  plan: (id: string) => ['treasury', 'instalment-plans', id] as const,
};

function Overdue({ figures }: { figures: InstalmentPlanFigures }) {
  if (!figures.overdue) return null;
  return (
    <span className={`pill ${figures.atRisk ? 'bad' : 'warn'}`}>
      {figures.overdue} {figures.overdue === 1 ? 'rata scaduta' : 'rate scadute'}
      {figures.atRisk && ' · rischio decadenza'}
    </span>
  );
}

/**
 * Tesoreria › Rateazioni (3.15, ADR-030): the debts paid in instalments to
 * the Agenzia delle Entrate, INPS, the Riscossione and the other bodies.
 */
export function InstalmentPlansPage() {
  usePageTitle('Rateazioni');
  const canWrite = useCan('tesoreria.write');
  const navigate = useNavigate();
  const plans = useQuery({
    queryKey: keys.all,
    queryFn: () => apiRequest<InstalmentPlanListItem[]>('/treasury/instalment-plans'),
  });
  const [creating, setCreating] = useState(false);
  return (
    <section className="stack">
      <div className="row between">
        <p className="muted small">
          Le rate sono scadenze: si pagano riconciliando il movimento della banca con «Rata di una
          rateazione»; quelle scadute e non pagate sono in Da sistemare.
        </p>
        {canWrite && (
          <button type="button" className="primary" onClick={() => setCreating(true)}>
            Nuova rateazione
          </button>
        )}
      </div>
      {plans.error && <p className="error">{plans.error.message}</p>}
      {plans.data && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rateazione</th>
                <th>Ente</th>
                <th className="numeric">Totale</th>
                <th className="numeric">Pagato</th>
                <th className="numeric">Residuo</th>
                <th>Prossima rata</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.data.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <Link to={`/tesoreria/rateazioni/${plan.id}`}>{plan.name}</Link>
                    {plan.reference && <small className="muted block">{plan.reference}</small>}
                  </td>
                  <td>
                    {INSTALMENT_BODY_LABELS[plan.body]}
                    <small className="muted block">{plan.subjectName}</small>
                  </td>
                  <td className="numeric">{formatMoney(plan.figures.total)}</td>
                  <td className="numeric">{formatMoney(plan.figures.paid)}</td>
                  <td className="numeric">
                    <strong>{formatMoney(plan.figures.residual)}</strong>
                  </td>
                  <td>
                    {plan.figures.next
                      ? `${plan.figures.next.number}ª del ${formatDate(plan.figures.next.dueDate)} · ${formatMoney(plan.figures.next.amount)}`
                      : 'pagata tutta'}
                  </td>
                  <td>
                    <Overdue figures={plan.figures} />
                  </td>
                </tr>
              ))}
              {plans.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    Nessuna rateazione.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {creating && (
        <InstalmentPlanForm
          plan={null}
          onClose={() => setCreating(false)}
          onSaved={(plan) => {
            setCreating(false);
            announceTreasuryChange();
            void navigate(`/tesoreria/rateazioni/${plan.id}`);
          }}
        />
      )}
    </section>
  );
}

/** A rateazione with its instalments, each with its state and the movement that paid it. */
export function InstalmentPlanPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canWrite = useCan('tesoreria.write');
  const plan = useQuery({
    queryKey: keys.plan(id),
    queryFn: () => apiRequest<InstalmentPlanResponse>(`/treasury/instalment-plans/${id}`),
  });
  usePageTitle(plan.data ? `Rateazione · ${plan.data.name}` : 'Rateazione');
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const remove = useMutation({
    mutationFn: () => apiRequest(`/treasury/instalment-plans/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      announceTreasuryChange();
      await queryClient.invalidateQueries({ queryKey: keys.all });
      void navigate('/tesoreria/rateazioni');
    },
  });
  if (plan.error) return <p className="error">{plan.error.message}</p>;
  const data = plan.data;
  if (!data) return <p className="muted">Caricamento…</p>;
  const paidAny = data.dues.some((due) => due.state === 'pagata');
  return (
    <section className="stack">
      <p className="muted small">
        <Link to="/tesoreria/rateazioni">Rateazioni</Link> › {data.name}
      </p>
      <div className="row between">
        <div>
          <h2>
            {data.name} <Overdue figures={data.figures} />
          </h2>
          <p className="muted">
            {INSTALMENT_BODY_LABELS[data.body]} ·{' '}
            <Link to={`/tesoreria/schede-contabili/${data.subjectId}`}>{data.subjectName}</Link>
            {data.reference && ` · ${data.reference}`} · {INSTALMENT_METHOD_LABELS[data.method]}
            {data.accountCode && ` da ${data.accountCode}`}
            {data.maxMissed && ` · avviso dopo ${data.maxMissed} rate scadute`}
          </p>
        </div>
        {canWrite && (
          <div className="row">
            <button type="button" onClick={() => setEditing(true)}>
              Modifica
            </button>
            {!paidAny && (
              <button type="button" className="danger" onClick={() => setDeleting(true)}>
                Elimina
              </button>
            )}
          </div>
        )}
      </div>
      <div className="treasury-tiles">
        <div className="treasury-tile">
          <span>Totale</span>
          <strong>{formatMoney(data.figures.total)}</strong>
        </div>
        <div className="treasury-tile">
          <span>Pagato</span>
          <strong>{formatMoney(data.figures.paid)}</strong>
        </div>
        <div className="treasury-tile">
          <span>Residuo</span>
          <strong>{formatMoney(data.figures.residual)}</strong>
        </div>
        <div className="treasury-tile">
          <span>Prossima rata</span>
          <strong>{data.figures.next ? formatDate(data.figures.next.dueDate) : '—'}</strong>
        </div>
      </div>
      {data.notes && <p className="pre-line">{data.notes}</p>}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Scadenza</th>
              <th className="numeric">Rata</th>
              <th className="numeric">di cui interessi</th>
              <th>Pagata con</th>
              <th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {data.dues.map((due) => (
              <tr key={due.id}>
                <td>{due.number}</td>
                <td>{formatDate(due.dueDate)}</td>
                <td className="numeric">{formatMoney(due.amount)}</td>
                <td className="numeric">{formatMoney(due.interest)}</td>
                <td>
                  {due.movement ? (
                    <Link to={movementPath(due.movement.accountCode, due.movement.id)}>
                      {due.movement.accountCode} del {formatDate(due.movement.bookingDate)}
                    </Link>
                  ) : due.f24 ? (
                    <Link to={`/tesoreria/f24/${due.f24.id}`}>
                      F24 del {formatDate(due.f24.dueDate)}
                    </Link>
                  ) : due.card ? (
                    <>
                      <Link
                        to={`/tesoreria/carte?spesa=${encodeURIComponent(due.card.movementId)}&data=${due.card.date}`}
                      >
                        Carta •••• {due.card.last4} del {formatDate(due.card.date)}
                      </Link>
                      {due.card.amount !== due.amount && (
                        <small className="muted block">
                          {formatMoney(due.card.amount)} con la commissione
                        </small>
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <span
                    className={`pill ${due.state === 'pagata' ? 'ok' : due.state === 'scaduta' ? 'bad' : 'muted'}`}
                  >
                    {INSTALMENT_STATE_LABELS[due.state]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <InstalmentPlanForm
          plan={data}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            announceTreasuryChange();
            await queryClient.invalidateQueries({ queryKey: keys.all });
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={`Eliminare «${data.name}»?`}
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate()}
          onCancel={() => setDeleting(false)}
        >
          <p className="small">
            La rateazione e le sue rate spariscono; resta nel Registro attività.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
